import { Check, Minus } from 'lucide-react'
import type { FileRecord } from '@/data/useProjectFiles'
import type { ProjectRecord } from '@/data/useProjects'
import { completionPercent, stageCounts } from '@/lib/progress'
import { STAGES } from '@/lib/stages'
import type { LatLng } from '@/types/project'
import type { Role } from '@/types/user'
import { StageCard } from './StageCard'

interface StageBoardProps {
  project: ProjectRecord
  files: FileRecord[]
  role: Role
  user: { uid: string; name: string }
}

/** Ten delivery stages as a step-by-step vertical timeline. */
export function StageBoard({ project, files, role, user }: StageBoardProps) {
  const total = STAGES.length
  const counts = stageCounts(project.stages)
  const percent = completionPercent(project.stages)

  // Expected route points for proof-of-product location checks.
  const expectedPoints: LatLng[] = [
    project.geo?.origin,
    project.geo?.delivery,
    ...(project.waypoints ?? []).map((wp) => ({ lat: wp.lat, lng: wp.lng })),
  ].filter((point): point is LatLng => point != null)

  const message =
    percent === 100
      ? '🎉 All stages complete!'
      : percent === 0
        ? "Let's get started — upload your first documents."
        : percent >= 70
          ? "Almost there — you're nearly done!"
          : 'Great progress — keep going!'

  return (
    <div>
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Delivery progress</span>
          <span className="text-2xl font-bold text-primary">{percent}%</span>
        </div>
        <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="mt-2 text-sm text-slate-500">
          {counts.green} of {counts.applicable} stages complete
          {counts.na > 0 ? ` · ${counts.na} N/A` : ''} — {message}
        </p>
      </div>

      <ol className="relative">
        {STAGES.map((stage, index) => {
          const status = project.stages[String(stage.id)]?.status ?? 'red'
          const completed = status === 'green'
          const notApplicable = status === 'na'
          const isLast = index === total - 1
          return (
            <li key={stage.id} className="relative flex gap-4 pb-6">
              {!isLast && (
                <span
                  aria-hidden
                  className={`absolute top-9 left-4 -ml-px h-full w-0.5 ${
                    completed ? 'bg-stage-green' : 'bg-slate-200'
                  }`}
                />
              )}
              <div
                className={`z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                  completed
                    ? 'bg-stage-green text-white'
                    : notApplicable
                      ? 'border-2 border-slate-200 bg-slate-100 text-slate-400'
                      : 'border-2 border-slate-300 bg-white text-slate-500'
                }`}
              >
                {completed ? (
                  <Check className="h-4 w-4" />
                ) : notApplicable ? (
                  <Minus className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </div>
              <div className="min-w-0 flex-1">
                <StageCard
                  projectId={project.id}
                  stage={stage}
                  status={status}
                  files={files.filter((file) => file.stage === stage.id)}
                  role={role}
                  user={user}
                  expectedPoints={expectedPoints}
                />
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
